import React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { GetApp } from "@mui/icons-material";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import * as am4charts from "@amcharts/amcharts4/charts";

interface ChartExportMenuProps {
  chart?: React.RefObject<am4charts.XYChart | null>;
  title: string;
  csvFieldMappings?: Record<string, string>;
}

enum ChartExportType {
  PNG = "png",
  CSV = "csv",
}

const getExportFileName = (
  type: ChartExportType,
  chartTitle: string
): string => {
  const sanitizedTitle = chartTitle.trim().replace(/\s+/g, "_");
  const [todayDate] = new Date().toISOString().split("T");

  return type === ChartExportType.PNG
    ? `${sanitizedTitle}_${todayDate}`
    : `${sanitizedTitle}Data_${todayDate}`;
};

const exportConfig = {
  scale: 3, // For 300 DPI
  sourceWidth: 1200,
  sourceHeight: 800,
  name: "",
};

export const ChartExportMenu: React.FC<ChartExportMenuProps> = ({
  chart,
  title,
  csvFieldMappings,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = React.useState(false);
  const open = Boolean(anchorEl);
  const originalDataFieldsRef = React.useRef<Record<string, string> | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Apply custom field mappings for CSV export
  const applyFieldMappings = (type: ChartExportType): void => {
    if (!chart?.current || type !== ChartExportType.CSV || !csvFieldMappings) {
      return;
    }

    // Store original dataFields to restore later
    originalDataFieldsRef.current = chart.current.exporting.dataFields || null;

    // Apply custom field mappings
    chart.current.exporting.dataFields = { ...csvFieldMappings };
  };

  // Restore original field mappings
  const restoreFieldMappings = (): void => {
    if (!chart?.current) return;

    if (originalDataFieldsRef.current) {
      chart.current.exporting.dataFields = originalDataFieldsRef.current;
    } else {
      // If there were no original mappings, remove the property
      delete chart.current.exporting.dataFields;
    }
    originalDataFieldsRef.current = null;
  };

  const handleExport = (type: ChartExportType) => async () => {
    if (!chart?.current) return;

    handleClose();
    chart.current.exporting.filePrefix = getExportFileName(type, title);
    applyFieldMappings(type);
    const exportOptions = {
      ...(type === ChartExportType.PNG ? exportConfig : {}),
      beforeCapture: () => {
        setIsExporting(true);
      },
      afterCapture: () => {
        setIsExporting(false);
      },
      // This callback is called when export fails or succeeds
      complete: () => {
        setIsExporting(false);
        // Restore original field mappings if CSV export
        if (type === ChartExportType.CSV) {
          restoreFieldMappings();
        }
      },
    };

    // Start export process
    chart.current.exporting.export(type, exportOptions);
  };

  return (
    <>
      <IconButton
        data-testid="chart-export-menu-btn"
        aria-label="export options"
        aria-controls={open ? `${title}-export-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="small"
      >
        {isExporting ? (
          <CircularProgress size={24} color="primary" />
        ) : (
          <GetApp />
        )}
      </IconButton>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          data-testid="chart-export-csv-option"
          onClick={handleExport(ChartExportType.CSV)}
        >
          <ListItemIcon>
            <GetApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as CSV</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="chart-export-png-option"
          onClick={handleExport(ChartExportType.PNG)}
        >
          <ListItemIcon>
            <GetApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as PNG</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
